import React, {useState} from 'react';
import {Pressable, View} from 'react-native';

// The app's own text atom. With react-native's Text the CTA rendered in the
// browser's system font while the sentence above it used DM Sans.
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';

import {F} from '../courseTheme';

type Variant = 'primary' | 'review';

type Props = {
  label: string;
  onPress?: () => void;
  disabled?: boolean;
  variant?: Variant;
  showArrow?: boolean;
  /** Renders the button at icon-only width, for the back/reset controls. */
  compact?: boolean;
  /**
   * Draws attention once an action becomes available. The LMS player shimmers
   * CONTINUE the moment a page is solved; this is the same signal without the
   * animation, and it replaces auto-advancing the student.
   */
  emphasised?: boolean;
};

const TOKENS: Record<Variant, {bg: string; border: string; text: string}> = {
  primary: {bg: F.yellow, border: F.yellowBorder, text: F.brown},
  review: {bg: '#FAF6EB', border: '#806234', text: F.brown},
};

/**
 * The chunky 3D button the LMS player uses for CONTINUE and HINT
 * (nscl/src/app/components/shared/CcButton.tsx): a flat fill over a thick
 * bottom border that compresses on press, so the button appears to depress
 * into the page.
 */
export default function CourseButton({
  label,
  onPress,
  disabled = false,
  variant = 'primary',
  showArrow = false,
  compact = false,
  emphasised = false,
}: Props) {
  const [pressed, setPressed] = useState(false);
  const t = TOKENS[variant];

  return (
    <Pressable
      accessibilityRole="button"
      onPress={disabled ? undefined : onPress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      disabled={disabled}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: F.radius.button,
        paddingHorizontal: compact ? 14 : 24,
        paddingVertical: 10,
        backgroundColor: t.bg,
        borderBottomWidth: pressed && !disabled ? 1 : 4,
        borderBottomColor: t.border,
        transform: [{translateY: pressed && !disabled ? 2 : 0}],
        // 0.6 left a disabled gold CTA looking live — the blocked Publish
        // button read as pressable. 0.45 is inside the 0.38-0.5 band the
        // platform guidelines use, and the cursor says so too.
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(emphasised && !disabled
          ? {borderWidth: 2, borderColor: F.white, borderBottomWidth: 4}
          : null),
      }}>
      <CCText
        style={{
          fontSize: 14,
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: 0.32,
          color: t.text,
          marginRight: showArrow ? 4 : 0,
          // Without this the label pushes the button wider than its parent —
          // chapter titles in the coach rail ran off its right edge.
          flexShrink: 1,
        }}>
        {label}
      </CCText>
      {showArrow ? (
        <Ionicons name="arrow-forward" size={16} color={t.text} />
      ) : null}
    </Pressable>
  );
}

/**
 * The square yellow icon button used for back / reset in the action row.
 * `glyph` is an Ionicons name, not a character: the Unicode arrows this used to
 * render are absent from DM Sans, so the browser fell back per-glyph to a
 * system symbol font at a different optical weight.
 */
export function CourseIconButton({
  glyph,
  onPress,
  disabled = false,
  label,
}: {
  glyph: string;
  onPress?: () => void;
  disabled?: boolean;
  label: string;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={disabled ? undefined : onPress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      disabled={disabled}
      style={{
        width: 48,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: F.radius.button,
        backgroundColor: F.yellow,
        borderBottomWidth: pressed && !disabled ? 1 : 4,
        borderBottomColor: F.yellowBorder,
        transform: [{translateY: pressed && !disabled ? 2 : 0}],
        // 0.6 left a disabled gold CTA looking live — the blocked Publish
        // button read as pressable. 0.45 is inside the 0.38-0.5 band the
        // platform guidelines use, and the cursor says so too.
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}>
      <Ionicons name={glyph} size={20} color={F.brown} />
    </Pressable>
  );
}

/** "White to play" / "Black to play", pinned to the bottom of the lesson card. */
export function TurnBadge({turn}: {turn: 'w' | 'b'}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 8,
        backgroundColor: F.badgeBg,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.05)',
        paddingHorizontal: 12,
        paddingVertical: 8,
      }}>
      <View
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          backgroundColor: turn === 'w' ? F.white : F.brownDark,
          borderWidth: 1,
          borderColor: F.brownDark,
        }}
      />
      <CCText style={{fontSize: 14, fontWeight: '600', color: F.brownDark}}>
        {turn === 'w' ? 'White to play' : 'Black to play'}
      </CCText>
    </View>
  );
}
